import type { LanguageDTO } from 'store/accounts/dtos';
import type { OrganizationPermission, OrganizationRole } from 'store/organizations/types';
import type { Nullable } from 'types';

import type { MultiFactorHardwareOptions, MultiFactorProvider, UserRole } from './types';

/**
 * @POST /login-check
 */
export type AuthenticationDTO = TokenDTO & {
  mfa: Nullable<MultiFactorDTO>;
};

/**
 * @POST /exchange-session-token
 */
export type AuthenticationExchangeDTO = TokenExchangeDTO & {
  mfa: Nullable<MultiFactorDTO>;
};

/**
 * @GET /captcha
 */
export type CaptchaDTO = {
  captchaEnabled: boolean;
  captchaSiteKey: Nullable<string>;
};

/**
 * @POST /login-check
 * @GET /2fa-status
 */
export type MultiFactorDTO = {
  providers: MultiFactorProvider[];
  preferredProvider: MultiFactorProvider;
  options: Nullable<MultiFactorHardwareOptions>;
};

/**
 * @POST /generate-session-token
 */
export type SessionTokenDTO = {
  sessionToken: string;
};

export type AffiliationTypeDTO = 'user' | 'b2b' | 'reseller';

/**
 * @GET /session
 */
export type SessionDTO = {
  userId: number;
  userIp: Nullable<string>;
  language: Nullable<LanguageDTO>;
  profileDetails: ProfileDetailsDTO;
  organisation: Nullable<OrganizationDetailsDTO>;
  roles: UserRole[];
  trialCount: number;
  trialLimit: number;
  createdAt: string;
  pendingCryptoOrders: number;
  isImpersonating: boolean;
  affiliationType: AffiliationTypeDTO;
};

export type OrganizationDetailsDTO = {
  id: string;
  name: string;
  role: OrganizationRole;
  permissions: OrganizationPermission[];
};

export type ProfileDetailsDTO = {
  email: string;
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  isActive: boolean;
  valid: boolean;
  isSuspended: boolean;
  suspensionReason?: Nullable<string>;
  suspensionReasonExplanation?: Nullable<string>;
  verificationRequest: VerificationRequestDTO;
  isProfileSkipped: boolean;
};

export enum VerificationStatusDTO {
  NOT_VERIFIED = 'NOT_VERIFIED',
  VERIFICATION_PENDING = 'VERIFICATION_PENDING',
  VERIFICATION_REQUIRED = 'VERIFICATION_REQUIRED',
  VERIFICATION_SUCCESSFUL = 'VERIFICATION_SUCCESSFUL',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
}

export type VerificationRequestDTO = {
  status: VerificationStatusDTO;
  reason: Nullable<string>;
};

/**
 * @POST /exchange-session-token
 */
export type TokenExchangeDTO = {
  accessToken: string;
  refreshToken: string;
};

/**
 * @POST /login-check
 * @POST /registration
 */
export type TokenDTO = {
  token: string;
  refreshToken: string;
};
